import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  downloadClicked,
  hdpiValueCopiedTapped,
  includeAndroidImagesOnChanged,
  includeIosImagesOnChanged,
  maxResolutionHeightOnChanged,
  maxResolutionWidthOnChanged,
  mdpiValueCopiedTapped,
  selectFileAsync,
  selectState,
  xhdpiValueCopiedTapped,
  xxhdpiValueCopiedTapped,
  xxxhdpiValueCopiedTapped,
} from "./AndroidIosImageResizerSlice";
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import FileSaveIcon from "@mui/icons-material/SaveOutlined";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { ResultCard } from "../../components/result_card/ResultCard";
import { HomeCard } from "../../components/home_card/HomeCard";
import FaviconGeneratorImage from "../../assets/FaviconGenerator.webp";
import ImageConverterImage from "../../assets/ImageConverter.webp";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function AndroidIosImageResizer() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  return (
    <SideMenuPageContainer
      pageTitle={t("androidIosResizer.title")}
      pageSeoTitle={t("androidIosResizer.seoTitle")}
      pageDescription={t("androidIosResizer.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("androidIosResizer.mainHeading")}
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Stack direction="column" justifyContent="center" alignItems="center">
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={true}
                    value={state.includeAndroidImages}
                    onChange={(e) => {
                      dispatch(
                        includeAndroidImagesOnChanged(Boolean(e.target.checked))
                      );
                      console.log(e.target.checked);
                    }}
                  />
                }
                label={t("androidIosResizer.androidImages")}
              />
              <FormControlLabel
                control={
                  <Switch
                    value={state.includeIosImages}
                    onChange={(e) => {
                      dispatch(
                        includeIosImagesOnChanged(Boolean(e.target.checked))
                      );
                    }}
                  />
                }
                label={t("androidIosResizer.iosImages")}
              />
            </FormGroup>
          </Stack>
        </Grid2>

        <Grid2 xs={12}>
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Button
              component="label"
              variant="contained"
              sx={{ width: 180 }}
              startIcon={<CloudUploadIcon sx={{ color: "#FFD9A8" }} />}
              onChange={(e: any) => {}}
            >
              <Typography>{t("androidIosResizer.selectPng")}</Typography>
              <VisuallyHiddenInput
                type="file"
                accept="image/png"
                onChange={(e: any) =>
                  dispatch(selectFileAsync(e.target.files[0]))
                }
              />
            </Button>
          </Stack>
        </Grid2>

        <Grid2
          xs={12}
          sx={{ display: state.imageSelected ? "inline" : "none" }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <ImageIcon sx={{ fontSize: "100px" }} />
            <Typography>{state.imageFileName}</Typography>
          </Stack>
        </Grid2>

        <Grid2
          xs={12}
          sx={{ display: state.imageSelected ? "inline" : "none" }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <TextField
              label={t("androidIosResizer.maxResolutionHeight")}
              variant="outlined"
              color="info"
              value={state.maxResolutionHeight}
              onChange={(e) => {
                dispatch(maxResolutionHeightOnChanged(e.target.value));
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>{t("androidIosResizer.px")}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid2>
        <Grid2
          xs={12}
          sx={{ display: state.imageSelected ? "inline" : "none" }}
        >
          <Stack direction="column" justifyContent="center" alignItems="center">
            <TextField
              label={t("androidIosResizer.maxResolutionWidth")}
              variant="outlined"
              color="info"
              value={state.maxResolutionWidth}
              onChange={(e) => {
                dispatch(maxResolutionWidthOnChanged(e.target.value));
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography>{t("androidIosResizer.px")}</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Grid2>

        <Grid2 xs={12}>
          <Stack direction="column" justifyContent="center" alignItems="center">
            <Box sx={{ mt: 3 }}>
              <CustomButton
                icon={<FileSaveIcon sx={{ color: "#FFD9A8" }} />}
                disabled={
                  !state.imageSelected ||
                  (!state.includeAndroidImages && !state.includeIosImages)
                }
                label={t("androidIosResizer.resize")}
                width={180}
                onClick={() => {
                  dispatch(downloadClicked());
                }}
              />
            </Box>
          </Stack>
        </Grid2>
        <Grid2 xs={12}>
        <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
          <Typography variant="h3" sx={{ mt: 5 }}>
            {t("androidIosResizer.content.performanceHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("androidIosResizer.content.performanceDescription")}
          </Typography>
          <Typography variant="h3" sx={{ mt: 3 }}>
            {t("androidIosResizer.content.androidHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("androidIosResizer.content.androidDescription")}{" "}
            <a
              href="https://developer.android.com/training/multiscreen/screendensities"
              title={t("androidIosResizer.content.androidLinkTitle")}
            >
              {" "}
              {t("androidIosResizer.content.androidLinkText")}
            </a>
            , it's essential to accommodate various screen pixel densities by
            supplying images in multiple resolutions. When only a single
            resolution is provided, Android is compelled to scale your image to
            fit other densities, which can lead to suboptimal performance or
            unsightly blurry artifacts. Manually resizing every image in your
            app can be a laborious and time-consuming process. This is precisely
            where our tool becomes invaluable, simplifying and streamlining the
            task of image resizing, ensuring crisp and clear visuals across all
            device types.
          </Typography>
          <ResultCard
            value={t("androidIosResizer.content.scalingFactor1x")}
            smallResult={true}
            description={t("androidIosResizer.content.mdpi")}
            symbol={""}
            resultCopied={state.mdpiValueCopied}
            onCopy={() => {
              dispatch(mdpiValueCopiedTapped());
            }}
          />
          <ResultCard
            value={t("androidIosResizer.content.scalingFactor15x")}
            smallResult={true}
            description={t("androidIosResizer.content.hdpi")}
            symbol={""}
            resultCopied={state.hdpiValueCopied}
            onCopy={() => {
              dispatch(hdpiValueCopiedTapped());
            }}
          />
          <ResultCard
            value={t("androidIosResizer.content.scalingFactor2x")}
            smallResult={true}
            description={t("androidIosResizer.content.xhdpi")}
            symbol={""}
            resultCopied={state.xhdpiValueCopied}
            onCopy={() => {
              dispatch(xhdpiValueCopiedTapped());
            }}
          />
          <ResultCard
            value={t("androidIosResizer.content.scalingFactor3x")}
            smallResult={true}
            description={t("androidIosResizer.content.xxhdpi")}
            symbol={""}
            resultCopied={state.xxhdpiValueCopied}
            onCopy={() => {
              dispatch(xxhdpiValueCopiedTapped());
            }}
          />
          <ResultCard
            value={t("androidIosResizer.content.scalingFactor4x")}
            smallResult={true}
            description={t("androidIosResizer.content.xxxhdpi")}
            symbol={""}
            resultCopied={state.xxxhdpiValueCopied}
            onCopy={() => {
              dispatch(xxxhdpiValueCopiedTapped());
            }}
          />
          <Typography variant="h3" sx={{ mt: 3 }}>
            {t("androidIosResizer.content.iosHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            <a
              href="https://developer.apple.com/design/human-interface-guidelines/images"
              title={t("androidIosResizer.content.iosLinkTitle")}
            >
              {t("androidIosResizer.content.iosLinkText")}
            </a>
            , it's crucial to provide images in three key resolutions to ensure
            optimal display across various Apple devices. These include the
            standard resolution (@1x), which is the baseline for older or
            lower-resolution devices. The @2x resolution is for Retina displays,
            which have twice the pixel density of standard displays, effectively
            doubling the resolution for sharper images. Finally, the @3x
            resolution caters to devices with even higher pixel densities, like
            some models of the iPhone and iPad, offering an even more refined
            and detailed visual experience. By incorporating these three
            resolutions, developers can guarantee that their app's visuals are
            crisp and clear, regardless of the device's screen specifications,
            thus enhancing the user experience.
          </Typography>
          <Typography variant="h3" sx={{ mt: 3 }}>
            {t("androidIosResizer.content.webAppHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("androidIosResizer.content.webAppDescription")}{" "}
            <a href="/ImageConverter" title={t("androidIosResizer.content.webAppLinkTitle")}>
              {t("androidIosResizer.content.webAppLinkText")}
            </a>{" "}
            to convert and resize images for maximum website performance and SEO
            improvements.
          </Typography>
          <Typography variant="body1" sx={{ mt: 3, mb: 3 }}>
            {t("androidIosResizer.content.faviconHeading")}{" "}
            <a
              href="/FaviconGenerator"
              title={t("androidIosResizer.content.faviconLinkTitle")}
            >
              {t("androidIosResizer.content.faviconLinkText")}
            </a>{" "}
            to generate favicons for your website or web application.
          </Typography>
        </Grid2>
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("androidIosResizer.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="faviconGenerator"
            link="/FaviconGenerator"
            smallCard={true}
            image={FaviconGeneratorImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="imageConverter"
            link="/ImageConverter"
            smallCard={true}
            image={ImageConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
